(() => {
  if (window.hollowProductContentInstalled) return;
  window.hollowProductContentInstalled = true;
  const loopResets = new WeakMap();
  const initializeLoop = (track, progress) => {
    const list = track.querySelector('.splide__list');
    const slides = [...(list?.children || [])];
    if (slides.length < 2) return null;
    const cloneSlide = slide => {
      const clone = slide.cloneNode(true);
      clone.dataset.hollowSliderClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      [clone, ...clone.querySelectorAll('[id]')].forEach(node => node.removeAttribute('id'));
      return clone;
    };
    list.prepend(...slides.map(cloneSlide));
    list.append(...slides.map(cloneSlide));
    let start = 0, cycle = 0, step = 0, active = 0, settleTimer, drag, touching = false;
    const modulo = value => ((value % slides.length) + slides.length) % slides.length;
    const update = () => {
      if (!step) return;
      active = modulo(Math.round((track.scrollLeft - start) / step));
      if (progress) progress.style.width = `${(active + 1) / slides.length * 100}%`;
    };
    const jump = left => {
      const behavior = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = left;
      track.style.scrollBehavior = behavior;
    };
    const settle = () => {
      clearTimeout(settleTimer);
      if (!track.isConnected || !cycle || drag || touching) return;
      let left = track.scrollLeft;
      while (left < start - .5) left += cycle;
      while (left >= start + cycle - .5) left -= cycle;
      if (Math.abs(left - track.scrollLeft) > .5) jump(left);
      update();
    };
    const refresh = () => {
      if (!track.clientWidth) return;
      step = slides[1].offsetLeft - slides[0].offsetLeft;
      if (!step) return;
      start = slides[0].offsetLeft;
      cycle = step * slides.length;
      jump(start + active * step);
      update();
    };
    track.addEventListener('scroll', () => {
      update();
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 140);
    }, { passive: true });
    track.addEventListener('scrollend', settle);
    track.addEventListener('touchstart', () => { touching = true; clearTimeout(settleTimer); }, { passive: true });
    const endTouch = event => {
      if (!touching) return;
      touching = [...event.touches].some(touch => track.contains(touch.target));
      if (touching) return;
      if (track.isConnected) settleTimer = setTimeout(settle, 180);
    };
    track.addEventListener('touchend', endTouch, { passive: true });
    track.addEventListener('touchcancel', endTouch, { passive: true });
    loopResets.set(track, () => {
      clearTimeout(settleTimer);
      touching = false;
      if (drag) track.style.scrollSnapType = drag.snap;
      drag = null;
    });
    track.addEventListener('pointerdown', event => {
      if (event.pointerType === 'touch' || event.button !== 0 || !cycle) return;
      drag = { id: event.pointerId, x: event.clientX, left: track.scrollLeft, moved: false, snap: track.style.scrollSnapType };
    });
    track.addEventListener('pointermove', event => {
      if (!drag || drag.id !== event.pointerId) return;
      const delta = event.clientX - drag.x;
      if (!drag.moved && Math.abs(delta) < 5) return;
      drag.moved = true;
      track.setPointerCapture?.(event.pointerId);
      track.style.scrollSnapType = 'none';
      event.preventDefault();
      track.scrollLeft = drag.left - delta;
    });
    const release = event => {
      if (!drag || drag.id !== event.pointerId) return;
      const moved = drag.moved;
      track.style.scrollSnapType = drag.snap;
      drag = null;
      if (track.hasPointerCapture?.(event.pointerId)) track.releasePointerCapture(event.pointerId);
      if (moved) {
        const left = start + Math.round((track.scrollLeft - start) / step) * step;
        track.scrollTo({ left, behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }
      settleTimer = setTimeout(settle, 180);
    };
    track.addEventListener('pointerup', release);
    track.addEventListener('pointercancel', release);
    track.addEventListener('dragstart', event => event.preventDefault());
    return refresh;
  };
  const initializeRelatedLoop = section => {
    const track = section.querySelector('.hollow-product-related__track');
    const list = track?.querySelector('.hollow-product-related__list');
    const slides = [...(list?.children || [])];
    if (slides.length < 2) return null;
    const cloneSlide = slide => {
      const clone = slide.cloneNode(true);
      clone.dataset.hollowRelatedClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      [clone, ...clone.querySelectorAll('*')].forEach(node => {
        ['id', 'for', 'form', 'name', 'aria-controls', 'aria-describedby', 'aria-labelledby'].forEach(attribute => node.removeAttribute(attribute));
        if (node.matches('a, button, input, select, textarea, [tabindex]')) node.tabIndex = -1;
      });
      return clone;
    };
    const before = slides.map(cloneSlide), after = slides.map(cloneSlide);
    list.prepend(...before);
    list.append(...after);
    let start = 0, cycle = 0, step = 0, active = 0, settleTimer, wheelTimer, wheelLocked = false, wheelDistance = 0, drag, blockedClickUntil = 0, touching = false;
    const modulo = value => ((value % slides.length) + slides.length) % slides.length;
    const jump = left => {
      const behavior = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = left;
      track.style.scrollBehavior = behavior;
    };
    const settle = () => {
      clearTimeout(settleTimer);
      if (!track.isConnected || !cycle || drag || touching) return;
      const left = start + ((track.scrollLeft - start) % cycle + cycle) % cycle;
      if (Math.abs(left - track.scrollLeft) > .5) jump(left);
      active = modulo(Math.round((left - start) / step));
    };
    const refresh = () => {
      if (!track.clientWidth) return;
      step = slides[1].offsetLeft - slides[0].offsetLeft;
      if (!step) return;
      start = slides[0].offsetLeft - list.firstElementChild.offsetLeft;
      cycle = step * slides.length;
      jump(start + active * step);
    };
    const move = direction => {
      if (!step) return;
      settle();
      const index = Math.round((track.scrollLeft - start) / step) + direction;
      track.scrollTo({ left: start + index * step, behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      settleTimer = setTimeout(settle, 450);
    };
    track.addEventListener('scroll', () => {
      if (step) active = modulo(Math.round((track.scrollLeft - start) / step));
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 160);
    }, { passive: true });
    track.addEventListener('scrollend', settle);
    track.addEventListener('touchstart', () => { touching = true; clearTimeout(settleTimer); }, { passive: true });
    const endTouch = event => {
      if (!touching) return;
      touching = [...event.touches].some(touch => track.contains(touch.target));
      if (touching) return;
      if (track.isConnected) settleTimer = setTimeout(settle, 180);
    };
    track.addEventListener('touchend', endTouch, { passive: true });
    track.addEventListener('touchcancel', endTouch, { passive: true });
    loopResets.set(track, () => {
      clearTimeout(settleTimer);
      clearTimeout(wheelTimer);
      touching = false;
      wheelLocked = false;
      wheelDistance = blockedClickUntil = 0;
      if (drag) track.style.scrollSnapType = drag.snap;
      drag = null;
      track.classList.remove('is-dragging');
    });
    track.addEventListener('wheel', event => {
      if (event.ctrlKey) return;
      let x = event.deltaX, y = event.deltaY;
      if (event.shiftKey && !x) { x = y; y = 0; }
      const horizontal = Math.abs(x) > Math.abs(y), distance = Math.abs(x);
      if (distance > 5 && (horizontal || event.shiftKey)) {
        event.preventDefault();
        if (!wheelLocked) {
          wheelDistance = Math.sign(wheelDistance) === Math.sign(x) ? wheelDistance + x : x;
          if (Math.abs(wheelDistance) > 25) { move(Math.sign(wheelDistance)); wheelLocked = true; }
        }
        clearTimeout(wheelTimer);
        wheelTimer = setTimeout(() => { wheelLocked = false; wheelDistance = 0; }, 300);
      } else if (distance < 5) { wheelLocked = false; wheelDistance = 0; }
    }, { passive: false });
    track.addEventListener('pointerdown', event => {
      if (event.pointerType === 'touch' || event.button !== 0 || event.isPrimary === false || !cycle) return;
      if (event.target.closest('.hollow-card-mobile-media, button, input, select, textarea')) return;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: track.scrollLeft, moved: false, snap: track.style.scrollSnapType };
    });
    track.addEventListener('pointermove', event => {
      if (!drag || drag.id !== event.pointerId) return;
      const delta = event.clientX - drag.x;
      if (!drag.moved && (Math.abs(delta) < 5 || Math.abs(delta) <= Math.abs(event.clientY - drag.y))) return;
      drag.moved = true;
      track.setPointerCapture?.(event.pointerId);
      track.classList.add('is-dragging');
      track.style.scrollSnapType = 'none';
      event.preventDefault();
      track.scrollLeft = drag.left - delta;
    });
    const release = event => {
      if (!drag || drag.id !== event.pointerId) return;
      const moved = drag.moved;
      track.style.scrollSnapType = drag.snap;
      track.classList.remove('is-dragging');
      drag = null;
      if (track.hasPointerCapture?.(event.pointerId)) track.releasePointerCapture(event.pointerId);
      if (moved) {
        blockedClickUntil = performance.now() + 500;
        const left = start + Math.round((track.scrollLeft - start) / step) * step;
        track.scrollTo({ left, behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }
      settleTimer = setTimeout(settle, 180);
    };
    track.addEventListener('pointerup', release);
    track.addEventListener('pointercancel', release);
    track.addEventListener('click', event => {
      if (performance.now() < blockedClickUntil) { event.preventDefault(); event.stopImmediatePropagation(); }
      blockedClickUntil = 0;
    }, true);
    track.addEventListener('dragstart', event => event.preventDefault());
    track.addEventListener('keydown', event => {
      if (event.target !== track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home' || event.key === 'End') {
        active = event.key === 'Home' ? 0 : slides.length - 1;
        jump(start + active * step);
      } else move(event.key === 'ArrowLeft' ? -1 : 1);
    });
    slides.forEach((slide, index) => new MutationObserver(() => {
      const first = cloneSlide(slide), last = cloneSlide(slide);
      before[index].replaceWith(first);
      after[index].replaceWith(last);
      before[index] = first;
      after[index] = last;
    }).observe(slide, { attributes: true, childList: true, characterData: true, subtree: true }));
    if (window.ResizeObserver) new ResizeObserver(refresh).observe(track);
    refresh();
    return move;
  };
  const initialize = (root = document) => {
    root.querySelectorAll('[data-hollow-product-content]').forEach(section => {
      if (section.dataset.hollowContentReady) return;
      section.dataset.hollowContentReady = 'true';
      const moveRelated = section.classList.contains('hollow-product-related--legacy') ? initializeRelatedLoop(section) : null;
      section.querySelectorAll('[data-hollow-product-slider]').forEach(slider => {
        const track = slider.querySelector('.splide__track');
        if (!track) return;
        track.tabIndex = 0;
        track.setAttribute('aria-label', 'Product benefits');
        const progress = section.querySelector('.ds-performance-perks__progress-bar');
        let config;
        try { config = JSON.parse(slider.dataset.sliderConfig || '{}'); } catch { config = {}; }
        const refreshLoop = config.type === 'loop' ? initializeLoop(track, progress) : null;
        const update = refreshLoop || (() => {
          const max = track.scrollWidth - track.clientWidth;
          if (progress) progress.style.width = `${max > 0 ? (track.scrollLeft + track.clientWidth) / track.scrollWidth * 100 : 100}%`;
        });
        if (!refreshLoop) track.addEventListener('scroll', update, { passive: true });
        if (window.ResizeObserver) new ResizeObserver(update).observe(track);
        update();
      });
      section.querySelectorAll('[data-hollow-ambient-video]').forEach(video => {
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        const toggle = visible => {
          if (visible && !reduced?.matches) video.play()?.catch(() => {}); else video.pause();
        };
        if (reduced?.matches) video.removeAttribute('autoplay');
        if (window.IntersectionObserver) new IntersectionObserver(entries => entries.forEach(entry => toggle(entry.isIntersecting))).observe(video);
        reduced?.addEventListener('change', () => toggle(false));
      });
      const dialog = section.querySelector('.hollow-product-video-dialog');
      let opener;
      const closeVideo = () => { const video = dialog?.querySelector('video'); if (video) { video.pause(); video.removeAttribute('src'); video.load(); } opener?.focus(); };
      if (dialog) {
        dialog.addEventListener('close', closeVideo);
        dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
      }
      section.addEventListener('click', event => {
        const relatedArrow = event.target.closest('[data-hollow-related-direction]');
        if (relatedArrow) {
          if (moveRelated) { moveRelated(Number(relatedArrow.dataset.hollowRelatedDirection)); return; }
          const track = section.querySelector('.hollow-product-related__track');
          const cards = [...(track?.querySelector('.hollow-product-related__list')?.children || [])];
          if (track && cards.length > 1) {
            const step = cards[1].offsetLeft - cards[0].offsetLeft;
            const max = track.scrollWidth - track.clientWidth;
            const next = track.scrollLeft + step * Number(relatedArrow.dataset.hollowRelatedDirection);
            const wraps = next < -1 || next > max + 1;
            track.scrollTo({ left: next < -1 ? max : next > max + 1 ? 0 : next, behavior: wraps || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
          }
        }
        const videoButton = event.target.closest('[data-hollow-product-video]');
        if (videoButton && dialog && !videoButton.disabled && videoButton.dataset.hollowProductVideo) {
          opener = videoButton;
          dialog.querySelector('video').src = videoButton.dataset.hollowProductVideo;
          dialog.showModal();
          const play = dialog.querySelector('video').play();
          if (play?.catch) play.catch(() => {});
        }
        if (event.target.closest('[data-hollow-product-video-close]')) dialog?.close();
        const swatch = event.target.closest('.js-compare-swatch');
        if (!swatch) return;
        const group = swatch.closest('[data-product-handle],.ds-product-comparison__product,.ds-product-comparison__product-card,.ds-product-comparison__mobile-card,.ds-product-comparison__header-col') || swatch.parentElement.parentElement;
        const image = group.querySelector('.js-compare-image,img');
        if (image && swatch.dataset.image) { image.removeAttribute('srcset'); image.src = swatch.dataset.image; }
        group.querySelectorAll('.js-compare-swatch').forEach(button => { const active = button === swatch; button.classList.toggle('is-active', active); button.setAttribute('aria-pressed', String(active)); });
        if (swatch.dataset.url) group.querySelectorAll('a[href*="/products/"]').forEach(link => { link.href = swatch.dataset.url; });
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize()); else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('hollow:product:updated', event => initialize(event.target));
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const next = data.html?.querySelector('[data-hollow-product-details]');
      if (!next) return;
      document.querySelectorAll('[data-hollow-product-details]').forEach(current => {
        if (current.dataset.hollowProductDetails !== String(data.sectionId)) return;
        current.querySelectorAll('[data-hollow-variant-detail]').forEach(disclosure => {
          const replacement = next.querySelector(`[data-hollow-variant-detail="${disclosure.dataset.hollowVariantDetail}"] .ds-accordion-content`);
          const content = disclosure.querySelector('.ds-accordion-content');
          if (replacement && content) content.innerHTML = replacement.innerHTML;
        });
      });
    });
  }
  new MutationObserver(records => {
    for (const record of records) {
      for (const node of record.removedNodes) if (node.nodeType === 1) {
        loopResets.get(node)?.();
        node.querySelectorAll('.splide__track, .hollow-product-related__track').forEach(track => loopResets.get(track)?.());
      }
      for (const node of record.addedNodes) if (node.nodeType === 1 && (node.matches('[data-hollow-product-content]') || node.querySelector('[data-hollow-product-content]'))) initialize(node.parentElement || document);
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
